import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { Presentation } from "../../../../core/gameflow/controller/Presentation";
import { GameData } from "../../../server/data/GameData";
import { SanEngineManager } from "../../../engine/SanEngineManager";
import { RevealerManager } from "../../../modules/revealer/RevealerManager";
import { ReelStoppedFlow, ReelStoppedPresentation } from "../ReelStoppedPresentation";
import { Revealer } from "../../../modules/revealer/Revealer";
import { ReavealerState } from "../../../modules/revealer/RevealSpinner";
import { ScatterRevealPresentation } from '../../features/scatter/ScatterRevealPresentation';
import { SoundManager } from "../../../config/sound/SoundManager";
import { SoundConfig } from "../../../config/sound/SoundConfig";
import { SanQuentin2 } from "../../../SanQuentin2";
import { StateHandler } from "../../../../core/utils/StateHandler";
import { SpinePlayer } from "../../../../core/displayobject/SpinePlayer";
import { GameConfig } from "../../../config/base/GameConfig";
import { TranslationConfig } from '../../../config/translate/TranslationConfig';
import { GameSetting } from "../../../../core/setting/GameSetting";
import { Sequence } from "../../../../core/displayobject/Sequence";
import { BLEND_MODES } from "pixi.js";
import { Helper } from "../../../../core/utils/Helper";

export class FSReelStoppedPresentation extends ReelStoppedPresentation {

    protected flowState: StateHandler = new StateHandler(ReelStoppedFlow.SPLIT, new Map<Number, Function>([
        [ReelStoppedFlow.EXTRAFS, () => this.presentExtraFS()],
        [ReelStoppedFlow.CONVERT, () => this.onConvert()],
        [ReelStoppedFlow.REVEAL, () => this.onReveal()],
        [ReelStoppedFlow.DOUBLEXWAYS, () => this.doDoubleXways()],
        [ReelStoppedFlow.SHOW, () => this.onNewBarSplit()],
        [ReelStoppedFlow.SPLIT, () => this.extraSplit()],
        [ReelStoppedFlow.EXPAND, () => this.expandXWays()],
        [ReelStoppedFlow.MYSTERYREV, () => this.revealMyst()]
    ]));

    protected newOpenedBars: number[] = [];

    public checkForActivation(data: GameData): boolean {
        return data.isFreeSpin;
    }

    public setGameData(data: GameData): void {
        super.setGameData(data);
        this.flowState.set(SanQuentin2.jumpingWildController.activeJW.length != this._gameData.raw.numJumpingWilds ?
            ReelStoppedFlow.CONVERT : ReelStoppedFlow.DOUBLEXWAYS);

        if (this._gameData.Reel.amountOfScatters >= 2 && !data.Win.brokeTheBank) {
            this.flowState.set(ReelStoppedFlow.EXTRAFS);
        }

        this.newOpenedBars = [];
    }

    public preStart(): void { }

    protected presentExtraFS(): void {

        SanEngineManager.RSAll((rc, sym) => {
            if (!sym.isScatter) {
                this.timeline.add(sym.markNonWin(true, 0.65, 0.5), 0)
            }
        });

        RevealerManager.getAllBars().forEach(bar => {
            if (bar.isActive) {
                this.timeline.add(() => { bar.markNonWin(true, 0.65, 0.5) }, 0)
            }
        })

        let flashDurr = this.timeline.duration();

        SanEngineManager.RSAll((rc, sym) => {
            if (sym.isScatter) {
                this.timeline.add(() => { sym.attentionFlash(); SoundManager.playSoundEffect(SoundConfig.FS_PLUS_SPINS); }, flashDurr);
                flashDurr += 0.2;
            }
        });

        const amm = Math.abs(this._gameData.raw.freespinsLeft - SanQuentin2.postCounter.getAmount);
        this.timeline.add(() => SanQuentin2.keypadPlugin.setZeroBetSpinCounter(this._gameData.raw.freespinsLeft));
        this.timeline.add(SanQuentin2.postCounter.playText(GameSetting.translate(TranslationConfig.FREESPINS_COLLECTED) + "\n" + amm));

        const durr = this.timeline.duration() - 0.25;
        SanEngineManager.RSAll((rc, sym) => {
            if (!sym.isScatter) {
                this.timeline.add(() => { sym.markNonWin(false) }, durr)
            }
        });

        RevealerManager.getAllBars().forEach(bar => {
            if (bar.isActive) {
                this.timeline.add(() => { bar.markNonWin(false, 0.65) }, durr)
            }
        })

        this.timeline.to(this, { duration: 0.2 });

        this.flowState.set(SanQuentin2.jumpingWildController.activeJW.length != this._gameData.raw.numJumpingWilds ?
            ReelStoppedFlow.CONVERT : ReelStoppedFlow.DOUBLEXWAYS);
    }

    public createPresentation(skip: Function) {
        skip(false);
        this.flowState.triggerState();
    }

    protected onConvert(): void {
        this.newOpenedBars = [];
        const openDurr: number = SpinePlayer.fetchDuration("S_normal", "transform");

        let playSound = true;

        SanEngineManager.RAll(rc => {
            if (this._gameData.Reel.scatterPositions[rc.reelID] >= 0 && !RevealerManager.getTopBar(rc.reelID).isActive) {
                let sym = rc.getSymbol(this._gameData.Reel.scatterPositions[rc.reelID])!;
                if (playSound) {
                    playSound = false;
                    SoundManager.playSoundEffect(SoundConfig.SCATTER_TO_WILD, undefined, true);
                }

                if (!RevealerManager.checkBothState(rc.reelID, ReavealerState.EXPANDING))
                    this.timeline.add(sym.onScatterToWild(true), 0);

                this.timeline.add(() => {
                    RevealerManager.getTopBar(rc.reelID).openBar(true);
                    RevealerManager.getBotBar(rc.reelID).openBar(true);
                }, openDurr * 0.75);

                this.newOpenedBars.push(rc.reelID);
                const jw = SanQuentin2.jumpingWildController.createJumpWild();
                jw.alpha = 0;
                jw.ReelID = rc.reelID;
                jw.RowID = sym.posID[0];
            }
        });

        if (this._gameData.raw.numJumpingWilds >= 3)
            SanQuentin2.background.showRedLight(true);

        this.flowState.set(ReelStoppedFlow.REVEAL);
    }

    protected onReveal(): void {
        this.newOpenedBars.forEach(id => {
            this.timeline.add(ScatterRevealPresentation.revealBar(id), 0);
        })
        this.flowState.set(ReelStoppedFlow.DOUBLEXWAYS);
    }

    public doDoubleXways(): void {
        let playSound: boolean = false;

        this._gameData.raw.topBottomReelOpened.forEach((v, i) => {
            if (v) {
                const revTop: Revealer = RevealerManager.getTopBar(i);
                const revBott: Revealer = RevealerManager.getBotBar(i);

                if (revTop.state.equal(ReavealerState.EXPANDING) && revBott.state.equal(ReavealerState.EXPANDING)) {
                    this.timeline.add(ScatterRevealPresentation.revealTwoMyst(i), 0);
                    playSound = true;
                }
            }
        })

        if (playSound) SoundManager.playSoundEffect(SoundConfig.XWAYS_SINGLE);
        this.flowState.set(ReelStoppedFlow.SHOW);
    }

    protected onNewBarSplit(): void {
        this.newOpenedBars.forEach(id => {
            const revTop: Revealer = RevealerManager.getTopBar(id);
            const revBott: Revealer = RevealerManager.getBotBar(id);

            const isRevTopNormal = revTop.state.equal(ReavealerState.NORMAL);
            const isRevBottNormal = revBott.state.equal(ReavealerState.NORMAL);
            const isRevTopSplitter = revTop.state.equal(ReavealerState.SPLITTER);
            const isRevBottSplitter = revBott.state.equal(ReavealerState.SPLITTER);

            if (isRevTopNormal && isRevBottNormal) {
                this.timeline.to(this, { duration: 0.05 }, 0);
            } else if (isRevTopSplitter && isRevBottSplitter) {
                const splitOffset: number = GameConfig.SPLIT_OFFSET;
                revTop.glowMult(); revBott.glowMult();

                const reel = SanEngineManager.getReel(id);
                const scatterPosition = this._gameData.Reel.scatterPositions[id];

                reel.AllWindowSymbols.forEach((s, i) => {
                    const triggerOffset = (i % 2) * splitOffset;
                    if (scatterPosition == s.posID[0]) {
                        this.timeline.add(s.MarkedForExtraSplit(true), triggerOffset);
                    } else {
                        this.timeline.add(s.doSplit(4), triggerOffset);
                    }
                });

                this.timeline.add(() => { SoundManager.playSoundEffect(SoundConfig.WILD_SPLIT) }, 0);
                this.timeline.to(this, { duration: 0.15 });
            } else {
                this.playFeaturePerBar(revTop, id);
                this.playFeaturePerBar(revBott, id);
            }
        });

        this.flowState.set(ReelStoppedFlow.SPLIT);
    }

    public extraSplit(): void {

        let playLineFireSound = true;
        if (this._gameData.Reel.specialSplits.length) {
            let delay = 0;
            let reelSplitIndx = -1;
            let reelRowSplitIndx: number[] = [];
            const wildSplitAnimDurr = SpinePlayer.fetchDuration("Wild", "split");
            SanEngineManager.RSWindow((rc, sym) => {
                if (this._gameData.Reel.specialSplits[rc.reelID][sym.posID[0]]) {
                    if (reelSplitIndx != rc.reelID && !reelRowSplitIndx.find(v => v == sym.posID[0]) && sym.wildExtraSplit) {
                        const item = SanQuentin2.overlay.playStandAloneSequence(SanQuentin2.assetLoader.getTextures("lineSplit"));
                        (item.item as Sequence).blendMode = BLEND_MODES.ADD;
                        item.item.position = Helper.copyLocalPosition(item.item, SanEngineManager.getReel(2));
                        item.item.position.y = Helper.copyLocalPosition(item.item, sym).y;
                        item.item.zIndex = 400;
                        item.item.scale.set(2.5);
                        item.item.visible = false;
                        if (rc.reelID > 2) item.item.scale.x *= -1;
                        reelSplitIndx = rc.reelID;
                        reelRowSplitIndx.push(sym.posID[0]);
                        item.timeline.pause();
                        this.timeline.add([() => { item.timeline.play(); item.item.visible = true; }], wildSplitAnimDurr);
                        this.timeline.to(this, { duration: item.timeline.duration() / 2 }, wildSplitAnimDurr);
                        if (playLineFireSound) {
                            playLineFireSound = false;
                            if (sym.isJumpWild) SoundManager.playSoundEffect(SoundConfig.MULT_UPGRADE);
                            SoundManager.playSoundEffect(SoundConfig.BONUS_WILD_SPLIT);
                        }
                    }
                    const splitSize = this._gameData.Reel.symbolSizeMap[rc.reelID][sym.posID[0] + 1];
                    this.timeline.add(sym.MarkedForExtraSplit(false)!, delay);
                    if (sym.splitAmm != splitSize)
                        this.timeline.add(() => { sym.doSplit(2, true, splitSize) }, wildSplitAnimDurr);
                }
            })
        }
        this.flowState.set(ReelStoppedFlow.EXPAND);
    }

    protected revealMyst(): void {

        SanEngineManager.RAll(r => {
            this.timeline.add(RevealerManager.getTopBar(r.reelID).expandRes(), 0);
            this.timeline.add(RevealerManager.getBotBar(r.reelID).expandRes(), 0);
        });

        if (this.timeline.duration()) {
            SoundManager.playSoundEffect(SoundConfig.XWAY_REVEAL_SINGLE);
        }
        this.setPlayable(false);
    }

    public done(): void {
        Logger.logDev("Stopped Done");
    }
}